import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { saveCommunityToStorage } from '../hooks/useCommunity';

interface AuthUser {
  userId: string;
  username: string;
  signInDetails?: {
    loginId?: string;
  };
}

interface UserProfile {
  name: string;
  email: string;
  picture: string | null;
  initial: string;
}

interface AuthContextValue {
  authUser: AuthUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  authUser: null,
  userProfile: null,
  loading: true,
  refreshAuth: async () => {},
});

async function fetchProfile(): Promise<UserProfile | null> {
  try {
    const session = await fetchAuthSession();
    const claims = session.tokens?.idToken?.payload;
    if (!claims) return null;
    const name = (claims['name'] as string) || (claims['email'] as string) || '';
    const email = (claims['email'] as string) || '';
    let picture = (claims['picture'] as string) || null;
    const initial = name.charAt(0).toUpperCase() || '?';

    // Prefer display_pic from our backend over OAuth picture
    try {
      const token = session.tokens?.idToken?.toString();
      if (token) {
        const res = await fetch('/api_profile', { headers: { Authorization: token } });
        if (res.ok) {
          const data = await res.json();
          if (data?.data?.display_pic) picture = data.data.display_pic;
          const { nationality, city } = data?.data || {};
          if (nationality && city) saveCommunityToStorage(nationality, city);
        }
      }
    } catch { /* silent — fall back to OAuth picture */ }

    return { name, email, picture, initial };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setAuthUser(user);
      const profile = await fetchProfile();
      setUserProfile(profile);
    } catch {
      setAuthUser(null);
      setUserProfile(null);
    }
  }, []);

  useEffect(() => {
    refreshAuth().finally(() => setLoading(false));

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn' || event === 'tokenRefresh') {
        refreshAuth();
      } else if (event === 'signedOut') {
        setAuthUser(null);
        setUserProfile(null);
      }
    });

    return () => unsubscribe();
  }, [refreshAuth]);

  return (
    <AuthContext.Provider value={{ authUser, userProfile, loading, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
